import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Paper,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { APIProvider } from '@vis.gl/react-google-maps';
import { LocationStickyProps } from '../../../pages/OurSchools';
import { locationsByState } from '../../../data/schoolLocations';
import { TourFormData } from '../ScheduleTourFlow';
import { useSearchParams } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { AutoTextSize } from 'auto-text-size';
import prodigyOwl from '../../prodigy-owl-logo.svg';
import microVpkOwl from '../../micro-vpk-logo.svg';
import { config } from '../../../config/env';

interface SchoolSelectionStepProps {
    selectedSchool: string;
    onNext: () => void;
    updateFormData: (data: Partial<TourFormData>) => void;
}

interface SchoolWithDistance extends LocationStickyProps {
    distance: number;
}

// Inner component that has access to Google Maps
function SchoolSelectionStepInner({ selectedSchool, onNext, updateFormData }: SchoolSelectionStepProps) {
    const [searchParams] = useSearchParams();
    const [school, setSchool] = useState(selectedSchool);
    const [addressInput, setAddressInput] = useState('');
    const [nearbySchools, setNearbySchools] = useState<SchoolWithDistance[]>([]);
    const [loadingNearby, setLoadingNearby] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
    const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

    // Get all locations for autocomplete, filtering out schools with tour scheduling disabled
    const allLocations = Object.entries(locationsByState).flatMap(([, locations]) =>
        locations
            .filter((loc) => loc.tourSchedulingEnabled !== false && !loc.customTourUrl) // Only show schools with internal tour scheduling
            .map((loc) => {
                const trimmedAddress2 = loc.address2.slice(0, -6); // Remove space + 5-digit ZIP
                return {
                    ...loc,
                    label: `${loc.name} - ${trimmedAddress2}`,
                    value: loc.schoolKey,
                };
            })
    );

    // Set school from URL params on mount
    useEffect(() => {
        const schoolParam = searchParams.get('school');
        if (schoolParam) {
            setSchool(schoolParam);
            const location = allLocations.find(loc => loc.value === schoolParam);
            if (location) {
                updateFormData({
                    school: schoolParam,
                    schoolName: location.name,
                    schoolAddress1: location.address1,
                    schoolAddress2: location.address2
                });
            }
        }
    }, [searchParams, allLocations, updateFormData]);

    // Calculate distance between two coordinates (Haversine formula)
    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 3959; // Earth's radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // Find 5 closest schools to the given coordinates
    // Note: This only searches through schools with tour scheduling enabled (allLocations is already filtered)
    const findNearbySchools = useCallback(async (userLat: number, userLng: number) => {
        setLoadingNearby(true);
        try {
            // Use cached coordinates to calculate distances
            const schoolsWithDistances: SchoolWithDistance[] = [];

            for (const location of allLocations) {
                // Use cached latitude/longitude if available
                if (location.latitude !== undefined && location.longitude !== undefined) {
                    const distance = calculateDistance(userLat, userLng, location.latitude, location.longitude);

                    schoolsWithDistances.push({
                        ...location,
                        distance
                    });
                }
            }

            // Sort by distance and get top 5
            const closest5 = schoolsWithDistances
                .sort((a, b) => a.distance - b.distance)
                .slice(0, 5);

            setNearbySchools(closest5);
        } catch (error) {
            console.error('Error finding nearby schools:', error);
        } finally {
            setLoadingNearby(false);
        }
    }, [allLocations]);

    // Setup Google Places Autocomplete
    useEffect(() => {
        if (!places || !inputRef.current) return;

        const autocomplete = new places.Autocomplete(inputRef.current, {
            fields: ['geometry', 'formatted_address'],
            componentRestrictions: { country: 'us' }
        });

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry?.location) {
                setAddressInput(place.formatted_address || '');
                void findNearbySchools(place.geometry.location.lat(), place.geometry.location.lng());
            }
        });

        autocompleteRef.current = autocomplete;

        // Initialize autocomplete service for manual predictions
        if (!autocompleteServiceRef.current) {
            autocompleteServiceRef.current = new places.AutocompleteService();
        }

        // Initialize places service for getting place details
        if (!placesServiceRef.current && inputRef.current) {
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new places.PlacesService(dummyDiv);
        }
    }, [places, findNearbySchools]);

    // Handle Enter key to auto-select first address
    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && addressInput && autocompleteServiceRef.current && placesServiceRef.current) {
            e.preventDefault();

            // Get predictions
            autocompleteServiceRef.current.getPlacePredictions(
                {
                    input: addressInput,
                    componentRestrictions: { country: 'us' }
                },
                (predictions, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions[0]) {
                        // Get details for the first prediction
                        const firstPrediction = predictions[0];

                        if (placesServiceRef.current) {
                            placesServiceRef.current.getDetails(
                                {
                                    placeId: firstPrediction.place_id,
                                    fields: ['geometry', 'formatted_address']
                                },
                                (place, detailsStatus) => {
                                    if (detailsStatus === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                                        setAddressInput(place.formatted_address || firstPrediction.description);
                                        findNearbySchools(place.geometry.location.lat(), place.geometry.location.lng());
                                    }
                                }
                            );
                        }
                    }
                }
            );
        }
    };

    // const handleNext = () => {
    //     if (school) {
    //         const location = allLocations.find(loc => loc.value === school);
    //         if (location) {
    //             updateFormData({ school, schoolName: location.name });
    //             onNext();
    //         }
    //     }
    // };

    const handleSchoolClick = (schoolKey: string, schoolName: string) => {
        setSchool(schoolKey);
        // Look up the school in the UNFILTERED set. `allLocations` excludes
        // schools with a customTourUrl, so we can't use it here or the redirect
        // would never fire for exactly the schools that need it.
        const allLocationsUnfiltered = Object.values(locationsByState).flat();
        const customTourLocation = allLocationsUnfiltered.find(loc => loc.schoolKey === schoolKey);
        if (customTourLocation?.customTourUrl != null) {
            window.location.replace(customTourLocation.customTourUrl);
            return;
        }
        const location = allLocations.find(loc => loc.value === schoolKey);
        updateFormData({
            school: schoolKey,
            schoolName,
            schoolAddress1: location?.address1,
            schoolAddress2: location?.address2
        });
        // Scroll to top before navigating
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Auto-advance to step 2
        onNext();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '700px', mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Which school would you like to tour?
            </Typography>

            {/* Address Search */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Find schools near you
                </Typography>
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    label="Enter your address"
                    variant="outlined"
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g., 123 Main St, Orlando, FL 32801"
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        '& .MuiInputBase-input': {
                            textAlign: 'left'
                        }
                    }}
                    InputProps={{
                        startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
            </Box>

            {/* Loading state */}
            {loadingNearby && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Nearby Schools */}
            {nearbySchools.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        5 Closest Schools
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {nearbySchools.map((nearbySchool) => (
                            <Paper
                                key={nearbySchool.schoolKey}
                                elevation={school === nearbySchool.schoolKey ? 6 : 2}
                                sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    backgroundColor: school === nearbySchool.schoolKey
                                        ? 'rgba(25, 118, 210, 0.1)'
                                        : 'rgba(255, 255, 255, 0.7)',
                                    border: school === nearbySchool.schoolKey ? '2px solid #1976d2' : 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                    }
                                }}
                                onClick={() => handleSchoolClick(nearbySchool.schoolKey, nearbySchool.name)}
                            >
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr auto',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    {/* School Photo/Avatar */}
                                    <Box sx={{
                                        width: 90,
                                        height: 90,
                                        minWidth: 90,
                                        minHeight: 90,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                    }}>
                                        <img
                                            src={nearbySchool.photo || (nearbySchool.microSchool ? microVpkOwl : prodigyOwl)}
                                            alt={nearbySchool.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: nearbySchool.photo ? 'cover' : 'contain'
                                            }}
                                        />
                                    </Box>

                                    {/* School Info */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                                        <Box sx={{ width: '100%', mb: 0.5 }}>
                                            <AutoTextSize
                                                maxFontSizePx={20}
                                                minFontSizePx={12}
                                                style={{
                                                    fontWeight: 'bold',
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {nearbySchool.name}
                                            </AutoTextSize>
                                        </Box>
                                        <Box sx={{ width: '100%' }}>
                                            <AutoTextSize
                                                maxFontSizePx={14}
                                                minFontSizePx={10}
                                                style={{
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    color: 'rgba(0, 0, 0, 0.6)',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {nearbySchool.address1}
                                            </AutoTextSize>
                                        </Box>
                                        <Box sx={{ width: '100%' }}>
                                            <AutoTextSize
                                                maxFontSizePx={14}
                                                minFontSizePx={10}
                                                style={{
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    color: 'rgba(0, 0, 0, 0.6)',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {nearbySchool.address2}
                                            </AutoTextSize>
                                        </Box>
                                        {nearbySchool.phone && (
                                            <Box sx={{ width: '100%' }}>
                                                <AutoTextSize
                                                    maxFontSizePx={14}
                                                    minFontSizePx={10}
                                                    style={{
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        color: '#00BFFF',
                                                        lineHeight: 1.2,
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {nearbySchool.phone}
                                                </AutoTextSize>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Distance Badge */}
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {nearbySchool.distance.toFixed(1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            miles
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                </Box>
            )}

            <Divider sx={{ my: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    OR BROWSE BY STATE
                </Typography>
            </Divider>

            {/* Browse Schools by State - Accordions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(locationsByState).map(([state, locations]) => {
                    const tourEnabledLocations = locations.filter((loc) => loc.tourSchedulingEnabled !== false);
                    // Skip rendering the accordion if no schools have tours enabled in this state
                    if (tourEnabledLocations.length === 0) return null;

                    return (
                        <Accordion
                            key={state}
                            sx={{
                                background: 'transparent',
                            }}
                            elevation={0}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    background: 'transparent',
                                    borderRadius: '20px',
                                    borderTopLeftRadius: '255px 15px',
                                    borderTopRightRadius: '15px 225px',
                                    borderBottomRightRadius: '225px 15px',
                                    borderBottomLeftRadius: '15px 255px',
                                    border: 'solid 3px black',
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold">
                                    {state.toUpperCase()}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ background: 'transparent', pt: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        width: '100%',
                                    }}
                                >
                                    {tourEnabledLocations.map((loc) => (
                                    <Paper
                                        key={loc.schoolKey}
                                        elevation={school === loc.schoolKey ? 6 : 2}
                                        sx={{
                                            p: 2,
                                            cursor: 'pointer',
                                            backgroundColor: school === loc.schoolKey
                                                ? 'rgba(25, 118, 210, 0.1)'
                                                : 'rgba(255, 255, 255, 0.7)',
                                            border: school === loc.schoolKey ? '2px solid #1976d2' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                            },
                                        }}
                                        onClick={() => handleSchoolClick(loc.schoolKey, loc.name)}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'auto 1fr',
                                                gap: 2,
                                            }}
                                        >
                                            {/* School Photo/Avatar */}
                                            <Box
                                                sx={{
                                                    width: 90,
                                                    height: 90,
                                                    minWidth: 90,
                                                    minHeight: 90,
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <img
                                                    src={loc.photo || (loc.microSchool ? microVpkOwl : prodigyOwl)}
                                                    alt={loc.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: loc.photo ? 'cover' : 'contain',
                                                    }}
                                                />
                                            </Box>

                                            {/* School Info */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                                                <Box sx={{ width: '100%', mb: 0.5 }}>
                                                    <AutoTextSize
                                                        maxFontSizePx={20}
                                                        minFontSizePx={12}
                                                        style={{
                                                            fontWeight: 'bold',
                                                            textAlign: 'left',
                                                            width: '100%',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {loc.name}
                                                    </AutoTextSize>
                                                </Box>
                                                <Box sx={{ width: '100%' }}>
                                                    <AutoTextSize
                                                        maxFontSizePx={14}
                                                        minFontSizePx={10}
                                                        style={{
                                                            textAlign: 'left',
                                                            width: '100%',
                                                            color: 'rgba(0, 0, 0, 0.6)',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {loc.address1}
                                                    </AutoTextSize>
                                                </Box>
                                                <Box sx={{ width: '100%' }}>
                                                    <AutoTextSize
                                                        maxFontSizePx={14}
                                                        minFontSizePx={10}
                                                        style={{
                                                            textAlign: 'left',
                                                            width: '100%',
                                                            color: 'rgba(0, 0, 0, 0.6)',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {loc.address2}
                                                    </AutoTextSize>
                                                </Box>
                                                {loc.phone && (
                                                    <Box sx={{ width: '100%' }}>
                                                        <AutoTextSize
                                                            maxFontSizePx={14}
                                                            minFontSizePx={10}
                                                            style={{
                                                                textAlign: 'left',
                                                                width: '100%',
                                                                color: '#00BFFF',
                                                                lineHeight: 1.2,
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            {loc.phone}
                                                        </AutoTextSize>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    );
                })}
            </Box>
        </Box>
    );
}

// Wrapper component with Google Maps API Provider
function SchoolSelectionStep(props: SchoolSelectionStepProps) {
    return (
        <APIProvider apiKey={config.googleMapsApiKey}>
            <SchoolSelectionStepInner {...props} />
        </APIProvider>
    );
}

export default SchoolSelectionStep;
